import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

export const searchHashtagVideos = async (hashtag, forceRefresh = false) => {
  try {
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cached = localStorage.getItem(`hashtag_${hashtag}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Check if cache is from today
        const today = new Date().toDateString();
        const cacheDate = new Date(timestamp).toDateString();
        if (today === cacheDate) {
          console.log('Returning cached results for:', hashtag);
          return data;
        }
      }
    }

    console.log('Making request to:', `${API_URL}/hashtag/videos`);
    console.log('Searching for hashtag:', hashtag);
    
    const response = await api.get('/hashtag/videos', {
      params: { hashtag }
    });
    
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('Response data:', response.data);
    
    if (!response.data?.videos) {
      console.error('Invalid response format - missing videos array:', response.data);
      throw new Error('Invalid response format from API');
    }
    
    console.log('Number of videos received:', response.data.videos.length);
    console.log('First video sample:', response.data.videos[0]);
    
    // Cache the new results
    const cacheData = {
      data: response.data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(`hashtag_${hashtag}`, JSON.stringify(cacheData));
    return response.data;
  } catch (error) {
    console.error('Error fetching hashtag videos:', error.response || error);
    throw error;
  }
};

export const getAIInsights = async (videos, analysisType = 'general') => {
  try {
    console.log(`Making request to: ${API_URL}/insights/ for ${analysisType} analysis`);
    const payload = { videos, type: analysisType };
    console.log('Request payload:', payload);
    const response = await api.post('/insights/', payload);
    console.log('Response status:', response.status);
    console.log('Response headers:', response.headers);
    console.log('AI Insights response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error getting AI insights:', error.response || error);
    throw error;
  }
};

export const analyzeComments = async (username, videoId) => {
  try {
    console.log(`Analyzing TikTok video comments for ID: ${videoId} (User: ${username})`);
    // Send both username and videoId to the backend
    const response = await api.post('/comments/', { username: username, videoId: videoId }); 
    console.log('Comment analysis response:', response.data);
    return response.data;
  } catch (error) {
    console.error('Error analyzing comments:', error.response || error);
    throw error;
  }
};

export const getProfileVideos = async (username, forceRefresh = false) => {
  try {
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cached = localStorage.getItem(`profile_${username}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Check if cache is from today
        const today = new Date().toDateString();
        const cacheDate = new Date(timestamp).toDateString();
        if (today === cacheDate) {
          console.log('Returning cached results for profile:', username);
          return data;
        }
      }
    }

    console.log('Making request to:', `${API_URL}/profile/videos`);
    console.log('Fetching videos for profile:', username);
    
    const response = await api.get('/profile/videos', {
      params: { username }
    });
    
    console.log('Response status:', response.status);
    console.log('Response data:', response.data);
    
    if (!response.data?.videos) {
      console.error('Invalid response format - missing videos array:', response.data);
      throw new Error('Invalid response format from API');
    }
    
    console.log('Number of videos received:', response.data.videos.length);
    
    // Cache the new results
    const cacheData = {
      data: response.data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(`profile_${username}`, JSON.stringify(cacheData));
    return response.data;
  } catch (error) {
    console.error('Error fetching profile videos:', error.response || error);
    throw error;
  }
};

export const analyzePostTiming = async (username, forceRefresh = false) => {
  try {
    // Check cache first if not forcing refresh
    if (!forceRefresh) {
      const cached = localStorage.getItem(`timing_${username}`);
      if (cached) {
        const { data, timestamp } = JSON.parse(cached);
        // Check if cache is from today
        const today = new Date().toDateString();
        const cacheDate = new Date(timestamp).toDateString();
        if (today === cacheDate) {
          console.log('Returning cached timing results for:', username);
          return data;
        }
      }
    }

    console.log('Making request to:', `${API_URL}/timing/analyze`);
    console.log('Analyzing timing for profile:', username);

    const response = await api.get('/timing/analyze', {
      params: { username }
    });

    console.log('Response status:', response.status);
    console.log('Response data:', response.data);

    if (!response.data?.bestTimes || !response.data?.weekdayStats) {
      console.error('Invalid response format:', response.data);
      throw new Error('Invalid response format from API');
    }

    // Cache the new results
    const cacheData = {
      data: response.data,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(`timing_${username}`, JSON.stringify(cacheData));
    return response.data;
  } catch (error) {
    console.error('Error analyzing post timing:', error.response || error);
    throw error;
  }
};

export const generateContentStrategy = async (topic) => {
  try {
    console.log('Making request to:', `${API_URL}/content-strategy/generate/`);
    console.log('Topic:', topic);

    const response = await api.post('/content-strategy/generate/', { topic });

    console.log('Response status:', response.status);
    console.log('Content strategy response:', response.data);

    if (!response.data?.ideas) {
      console.error('Invalid response format - missing ideas array:', response.data);
      throw new Error('Invalid response format from API');
    }

    return response.data;
  } catch (error) {
    console.error('Error generating content strategy:', error.response || error);
    throw error;
  }
};

export default api;
